import axios from 'axios'
import { FacturationGroup, UserPennylaneFacture } from './facturation'
import { CgvDataJson } from './cgvDataJsonForm'
import { v4 } from 'uuid'

export interface SoftaccessFactu {
  orga_id: string
  softaccess_id: string
  softaccess_type: 'cnegoce' | 'mspro'
  softaccess_name: string
  facturation_id?: string | null
  facturation_name?: string | null
  facturation_email?: string | null
  facturation_group_id?: string | null
  groupement?: string | null
  nb_facturation: number
  tva_enable: boolean
  price: number
  allSoftAgencies: { id: string; name: string }[]
  cgvs?: {
    cgv_id: string
    orga_id: string
    softaccess_id: string
    softaccess_type?: string
    softaccess_name?: string
    cgv_validated?: boolean
    cgv_version: number
    data_json?: string | CgvDataJson | null
  } | null
}

export interface CnGroupement {
  id: string
  name: string
  softaccessType: string
  prices: Record<string, number>
}

export interface FacturationCgvPayload {
  orga_id: string
  orga_siret: string | null
  orga_name: string
  softaccesses: SoftaccessFactu[]
  facturationGroups: FacturationGroup[]
  rg2s: UserPennylaneFacture[]
  cnGroupements: CnGroupement[]
}

export interface SaveFacturationPayload {
  id: string
  orga_id: string
  softaccess_id: string
  payment_method: string
  payment_id?: string
  payment_date?: Date | string
  tva_enable: boolean
  tva_international_number?: string
  payment_done: boolean
  credit_safe: boolean
  register_payment_limit_date?: Date | string
  default_group_facturation?: boolean
  email_facturation?: string
  ex_facture: boolean
}

export interface CgvPayload {
  cgv_id: string
  orga_id: string
  softaccess_id: string
  softaccess_type: string
  softaccess_name?: string
  cgv_collaborator?: string
  cgv_validated: boolean
  cgv_version: number
  data_json?: string
}

export interface SiretLookupResult {
  ok: boolean
  error?: string
  raison_sociale?: string
  forme_juridique?: string
  capital_social?: string | null
  address?: string
  postal_code?: string
  city?: string
  name_rcs?: string
  siren?: string
  siret?: string
}

export const facturationCgvService = {
  getFacturationCgvByOrga(orgaId: string) {
    return axios.get<FacturationCgvPayload>(`/organisations/${orgaId}/facturation-cgv`)
  },

  getInfosByOrga() {
    return axios.get('/api/facturation/infosByOrga')
  },

  saveFacturation(orgaId: string, softaccessType: string, softaccessId: string, email: string) { 
    const payload: SaveFacturationPayload = {
      id: v4(),
      orga_id: orgaId,
      softaccess_id: softaccessId,
      payment_method: '',
      payment_id: '',
      payment_date: undefined,
      tva_enable: true,
      tva_international_number: undefined,
      payment_done: false,
      credit_safe: false,
      register_payment_limit_date: undefined,
      default_group_facturation: undefined,
      email_facturation: email,
      ex_facture: false
    }
    return axios.post(`/facturation/${orgaId}/save`, payload, {
      params: { softaccess_type: softaccessType },
    })
  },

  updateGroupement(softaccessId: string, groupementId: string | undefined) {
    return axios.post('/api/facturation/updateGroupement', {
      sid: softaccessId,
      groupement_id: groupementId,
    })
  },

  updateTva(id: string, tvaEnable: boolean) {
    return axios.post('/api/facturation/updateTva', {
      id,
      tva_enable: tvaEnable,
    })
  },

  updateIsEx(id: string, isEx: boolean) {
    return axios.post('/api/facturation/updateIsEx', {
      id,
      ex_facture: isEx,
    })
  },

  facturer(selectedKeys?: (string | number)[]) {
    return axios.post(`/api/facturation/facturer`, selectedKeys)
  },

  getAvailableNotUsedPennylaneFacturation(orgaId: string, groupIdsUsed: string[]) {
    return axios.get<{ id: string; name: string }[]>(
      '/api/facturation/getAvailableNotUsedPennylaneFacturation',
      {
        params: {
          orga_id: orgaId,
          group_ids_used: groupIdsUsed.join(','),
        },
      }
    )
  },

  saveFacturationGroups(payload: {
    orga_id: string
    softaccess_id: string
    groups: FacturationGroup[]
    rg2s: UserPennylaneFacture[]
  }) {
    return axios.post('/api/facturation/saveFacturationGroups', payload)
  },

  saveCgv(orgaId: string, cgv: CgvPayload) {
    return axios.post(`/cgv/${orgaId}/save`, cgv)
  },

  updateCgv(orgaId: string, cgv: CgvPayload | Record<string, unknown>) {
    return axios.post(`/cgv/${orgaId}/update`, cgv, {
      params: { orga_id: orgaId },
    })
  },

  sendCgv(orgaId: string, cgvId: string) {
    return axios.post<{ ok?: boolean; error?: string }>(
      `/cgv/${orgaId}/send`,
      { cgv_id: cgvId },
      { validateStatus: s => s < 500 }
    )
  },

  lookupSiret(siret: string) {
    return axios.get<SiretLookupResult>(`/api/siret/${siret}`, {
      validateStatus: s => s < 500,
    })
  },
}
